import React from 'react';

import { Typography } from 'antd';
import moment from 'moment';
import { history } from 'umi';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { createEnumOptions } from '@/utils/core/data-transformation';
import { getEnumTranslationKey } from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import {
  createTableActionsColumn,
  formatChatThreadLabel,
  formatTZLocal,
  getEnumNamespace,
} from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import { formatCurrency, formatNumber } from '@/utils/output/formatters';
import Filters, { registerModuleFilters } from '@/utils/input/filters';

import { applyProcessInstanceDetailRow } from '../utils/applyProcessInstanceDetailRow';
import { MODEL_NAME } from './constants';

const MONTH_LABEL_KEYS = [
  'global.text.month.january',
  'global.text.month.february',
  'global.text.month.march',
  'global.text.month.april',
  'global.text.month.may',
  'global.text.month.june',
  'global.text.month.july',
  'global.text.month.august',
  'global.text.month.september',
  'global.text.month.october',
  'global.text.month.november',
  'global.text.month.december',
];

const buildMonthOptions = () =>
  MONTH_LABEL_KEYS.map((label, index) => ({
    value: index + 1,
    label,
  }));

const formatMonthLabel = (month, formatMessage) => {
  if (!month || month < 1 || month > 12) return DEFAULT_EMPTY_VALUE;
  return formatMessage({ id: MONTH_LABEL_KEYS[month - 1] });
};

const formatTokenValue = (value) => (value != null ? formatNumber(value) : '0');

const YearFilter = {
  type: 'select',
  name: 'year',
  label: 'aiConsumptions.text.year',
  locale: true,
  options: [...Array(10)].map((v, i) => {
    const year = +moment().format('YYYY') - i;
    return { value: year, label: year.toString() };
  }),
  allowClear: true,
  filterSearch: true,
};

const MonthFilter = {
  type: 'select',
  name: 'month',
  label: 'global.text.month',
  locale: true,
  options: buildMonthOptions(),
  allowClear: true,
  filterSearch: true,
};

const SourceFilter = {
  ...Filters.BaseFilters.EnumFilter,
  name: 'source',
  label: 'global.text.source',
  enumNamespace: getEnumNamespace(CoreEnums.AIConsumptionSource, 'Pricing'),
  enumSource: CoreEnums.AIConsumptionSource,
  options: createEnumOptions(CoreEnums.AIConsumptionSource, 'aiConsumptions.text.source'),
};

const ModelFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'modelId',
  label: 'aiConsumptions.text.model',
  apiRoute: 'ai/model/paged',
  filterSearch: true,
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const AgentFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'aiAgentId',
  label: 'global.text.agent',
  apiRoute: 'ai/agent/allowed',
  filterSearch: true,
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const ChatThreadFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'chatThreadId',
  label: 'aiConsumptions.text.chatThread',
  apiRoute: 'chat/thread/paged',
  uuidRouteFragments: ['aiAgentId'],
  uuidRouteFragmentsOptional: true,
  filterSearch: true,
  params: {
    select: 'id,number,name',
    orderBy: 'number asc',
  },
  nomenclatureMapRemoteData: (data) =>
    (data || []).map((item) => ({
      ...item,
      label: formatChatThreadLabel(item) || item.name || item.label,
    })),
};

const BillingMetricFilter = {
  ...Filters.BaseFilters.EnumFilter,
  name: 'billingMetric',
  label: 'aiConsumptions.text.billingMetric',
  enumNamespace: getEnumNamespace(CoreEnums.AIConsumptionBillingMetric, 'Pricing'),
  enumSource: CoreEnums.AIConsumptionBillingMetric,
  options: createEnumOptions(
    CoreEnums.AIConsumptionBillingMetric,
    'aiConsumptions.text.billingMetric',
  ),
};

const ProcessFilter = {
  ...Filters.ProcessFilter,
  filterSearch: true,
};

const ConnectionFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'connectionId',
  label: 'aiConsumptions.text.connection',
  apiRoute: 'connection/paged',
  filterSearch: true,
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const AppFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'appId',
  label: 'aiConsumptions.text.app',
  apiRoute: 'app/allowed',
  filterSearch: true,
  params: {
    select: 'id,name',
    filter: 'kind eq 1',
    orderBy: 'name asc',
  },
};

const UserFilter = {
  ...Filters.UserFilter,
  filterSearch: true,
};

const CreatedDateFilter = {
  ...Filters.DateFilter,
  label: 'global.text.createdDate',
};

export const filtersConfig = [
  YearFilter,
  MonthFilter,
  SourceFilter,
  ModelFilter,
  AppFilter,
  ProcessFilter,
  ConnectionFilter,
  AgentFilter,
  ChatThreadFilter,
  BillingMetricFilter,
  UserFilter,
  CreatedDateFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

/**
 * Domain-only detail mapping (month name, USD price, token math, process link).
 * Source enum badge comes from `_form.js` select + locale (same as environments).
 */
export function mapAiConsumptionDetailFields(rows, { item, formatMessage }) {
  if (!item || !rows?.length) return rows;

  const withProcessLink = applyProcessInstanceDetailRow(rows, item, history);

  return withProcessLink.map((row) => {
    if (row.name === 'month') {
      return { ...row, value: formatMonthLabel(item.month, formatMessage) };
    }

    if (row.name === 'finalPrice') {
      if (item.finalPrice == null && item.finalPrice !== 0) {
        return { ...row, value: DEFAULT_EMPTY_VALUE };
      }
      return {
        ...row,
        value: formatCurrency(item.finalPrice, 'USD', { maximumFractionDigits: 6 }),
      };
    }

    if (row.name === 'inputTokens') {
      return {
        ...row,
        value: formatTokenValue((item.inputTokens || 0) - (item.inputTokensCache || 0)),
      };
    }

    if (row.name === 'inputTokensCache') {
      return { ...row, value: formatTokenValue(item.inputTokensCache) };
    }

    if (row.name === 'outputTokens') {
      return { ...row, value: formatTokenValue(item.outputTokens) };
    }

    if (row.name === 'totalTokens') {
      return { ...row, value: formatTokenValue(item.totalTokens) };
    }

    if (row.name === 'user') {
      const user = item.user;
      const fullName = user?.fullName ?? row.value;
      const id = user?.id;
      if (!fullName && !id) return row;

      return {
        ...row,
        viewComponent: 'user',
        value: fullName,
        viewProps: { id, fullName },
      };
    }

    if (row.name === 'chatThread') {
      const label = formatChatThreadLabel(item.chatThread);
      if (!label) return row;
      return { ...row, value: label, locale: false };
    }

    return row;
  });
}

export function buildAiConsumptionColumns({
  formatMessage,
  openDrawerCreateOrEdit,
  searchCriteria,
}) {
  const highlight = createHighlightRender(searchCriteria);

  return [
    {
      key: 'created',
      title: formatMessage({ id: 'global.created' }),
      dataIndex: 'created',
      sorter: true,
      render: (value) => (value ? formatTZLocal(value) : DEFAULT_EMPTY_VALUE),
    },
    {
      key: 'year',
      title: formatMessage({ id: 'aiConsumptions.text.year' }),
      dataIndex: 'year',
      sorter: true,
    },
    {
      key: 'month',
      title: formatMessage({ id: 'global.text.month' }),
      dataIndex: 'month',
      sorter: true,
      render: (month) => formatMonthLabel(month, formatMessage),
    },
    {
      key: 'source',
      title: formatMessage({ id: 'global.text.source' }),
      dataIndex: 'source',
      sorter: true,
      align: 'center',
      render: (source) => {
        if (source == null) return DEFAULT_EMPTY_VALUE;
        const translationKey = getEnumTranslationKey(
          CoreEnums.AIConsumptionSource,
          source,
          'aiConsumptions.text.source',
        );
        return (
          <Badge status="default" type="process" text={formatMessage({ id: translationKey })} />
        );
      },
    },
    {
      key: 'model/name',
      title: formatMessage({ id: 'aiConsumptions.text.model' }),
      dataIndex: 'model',
      sorter: true,
      render: (value) => highlight(value?.name ?? DEFAULT_EMPTY_VALUE),
    },
    {
      key: 'family',
      title: formatMessage({ id: 'aiConsumptions.text.family' }),
      dataIndex: 'family',
      sorter: true,
      render: highlight,
    },
    {
      key: 'totalTokens',
      title: formatMessage({ id: 'aiConsumptions.text.totalTokens' }),
      dataIndex: 'totalTokens',
      sorter: true,
      align: 'right',
      render: (value) => (value ? value.toLocaleString() : DEFAULT_EMPTY_VALUE),
    },
    {
      key: 'finalPrice',
      title: formatMessage({ id: 'aiConsumptions.text.finalPrice' }),
      dataIndex: 'finalPrice',
      sorter: true,
      align: 'right',
      render: (value) => {
        if (!value && value !== 0) return DEFAULT_EMPTY_VALUE;
        return formatCurrency(value, 'USD', { maximumFractionDigits: 6 });
      },
    },
    {
      key: 'processInstance/name',
      title: formatMessage({ id: 'aiConsumptions.text.processInstance' }),
      dataIndex: 'processInstance',
      sorter: true,
      width: 200,
      ellipsis: true,
      render: (value, record) => {
        if (!value?.name) return DEFAULT_EMPTY_VALUE;

        const appUrl = `/applications/${record.processInstance?.id}`;
        return (
          <Typography.Link
            href={appUrl}
            onClick={(e) => {
              e.preventDefault();
              history.push(appUrl);
            }}
          >
            {highlight(value.name)}
          </Typography.Link>
        );
      },
    },
    {
      key: 'chatThread/number',
      title: formatMessage({ id: 'aiConsumptions.text.chatThread' }),
      dataIndex: 'chatThread',
      sorter: true,
      render: (value) => highlight(formatChatThreadLabel(value) ?? DEFAULT_EMPTY_VALUE),
    },
    createTableActionsColumn({
      inlineActionCount: 1,
      render: (el) => (
        <TableRowButtons
          onDetail={() => openDrawerCreateOrEdit(el.id, true)}
          formatMessage={formatMessage}
          actionOrder={{ primary: ['detail'], secondary: [] }}
        />
      ),
    }),
  ];
}
